import React, { useState, useEffect } from 'react';
import apiService from '../../../services/apiService';
import { toast } from 'react-toastify';

const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);

  // Fetch current settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const response = await apiService.get('/settings');

        if (response?.success && response.data) {
          setSettings(response.data);
        } else {
          toast.error('Invalid settings response from server');
        }
      } catch (err) {
        console.error('Failed to load settings:', err);
        toast.error('Failed to load application settings');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Universal handler that supports both direct values and functional updates
  const handleChange = (section, field, valueOrFn) => {
    setSettings(prev => {
      if (!prev) return prev;

      const newValue =
        typeof valueOrFn === 'function'
          ? valueOrFn(prev[section][field])
          : valueOrFn;

      // Special logic: only one payment mode can be enabled at a time
      if (section === 'payment') {
        if (field === 'oneTimeEnabled' && newValue === true) {
          return {
            ...prev,
            payment: {
              ...prev.payment,
              oneTimeEnabled: true,
              recurringEnabled: false,
            },
          };
        }
        if (field === 'recurringEnabled' && newValue === true) {
          return {
            ...prev,
            payment: {
              ...prev.payment,
              oneTimeEnabled: false,
              recurringEnabled: true,
            },
          };
        }
      }

      // Normal update for all other fields
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: newValue,
        },
      };
    });
  };

  const handleSave = async () => {
    if (!settings) return;

    // Validation: at least one payment mode must be enabled
    if (!settings.payment.oneTimeEnabled && !settings.payment.recurringEnabled) {
      toast.error('At least one payment mode (One-Time or Recurring) must be enabled');
      return;
    }

    try {
      setSaving(true);
      const response = await apiService.put('/settings', settings);

      if (response?.success) {
        toast.success('Settings updated successfully');
        // Use the returned data from server (recommended)
        if (response.data) {
          setSettings(response.data);
        }
      } else {
        toast.error('Server returned unsuccessful response');
      }
    } catch (err) {
      console.error('Failed to save settings:', err);
      toast.error(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Application Settings</h1>

      {/* Payment Settings */}
      <section className="mb-12 bg-white rounded-xl shadow-md overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Payment Configuration</h2>
        </div>
        <div className="p-6 space-y-8">
          {/* One-Time Payment */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">One-Time Payment</h3>
              <p className="text-sm text-gray-500">
                Allow users to purchase fixed-period access with one payment
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.payment.oneTimeEnabled}
                onChange={() => handleChange('payment', 'oneTimeEnabled', prev => !prev)}
                className="sr-only peer"
              />
              <div
                className={`w-14 h-7 rounded-full peer after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all ${
                  settings.payment.oneTimeEnabled
                    ? 'bg-green-600 after:translate-x-7'
                    : 'bg-gray-200'
                }`}
              ></div>
            </label>
          </div>

          {/* Recurring Subscription */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Recurring Subscription</h3>
              <p className="text-sm text-gray-500">
                Enable monthly/yearly automatic billing
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.payment.recurringEnabled}
                onChange={() => handleChange('payment', 'recurringEnabled', prev => !prev)}
                className="sr-only peer"
              />
              <div
                className={`w-14 h-7 rounded-full peer after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all ${
                  settings.payment.recurringEnabled
                    ? 'bg-green-600 after:translate-x-7'
                    : 'bg-gray-200'
                }`}
              ></div>
            </label>
          </div>
        </div>
      </section>

      {/* Notifications */}
      <section className="mb-12 bg-white rounded-xl shadow-md overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Notifications</h2>
        </div>
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Email Notifications</h3>
              <p className="text-sm text-gray-500">Send system emails to users</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.notifications.enableEmail}
                onChange={() => handleChange('notifications', 'enableEmail', prev => !prev)}
                className="sr-only peer"
              />
              <div
                className={`w-14 h-7 rounded-full peer after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all ${
                  settings.notifications.enableEmail
                    ? 'bg-indigo-600 after:translate-x-7'
                    : 'bg-gray-200'
                }`}
              ></div>
            </label>
          </div>
        </div>
      </section>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`
            px-8 py-3 rounded-lg font-medium text-white
            ${saving
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-indigo-600 hover:bg-indigo-700 transition-colors'}
          `}
        >
          {saving ? (
            <span className="flex items-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Saving...
            </span>
          ) : (
            'Save All Changes'
          )}
        </button>
      </div>
    </div>
  );
};

export default SettingsPage;